import type { FrameRecoveryProvider, RecoveryInput, RecoveryOutcome } from "@perceptron/core";

import { analytics } from "@demo/libs/analytics";
import { ANALYTICS_EVENTS } from "@demo/libs/analytics/types";
import { demoModelApi, type StreamRunRequest } from "@demo/libs/api/demoApiClient";
import { RECOVERY_PROMPT_VERSION } from "@demo/server/api/contracts/recover-track";

/** Identity of the output being viewed, independent of the next run's draft settings. */
export interface OutputRunContext {
  readonly model: StreamRunRequest["model"];
  readonly modelLabel: string;
  readonly sessionUuid: string;
  readonly parentRunUuid: string;
  readonly sourceIds: readonly string[];
}

function freezeSnapshot<T>(value: T): T {
  if (value && typeof value === "object") {
    Object.values(value).forEach(freezeSnapshot);
    Object.freeze(value);
  }
  return value;
}

export function createOutputRunContext(
  request: StreamRunRequest,
  modelLabel = request.model.publicName,
): OutputRunContext {
  return freezeSnapshot({
    model: structuredClone(request.model),
    modelLabel,
    sessionUuid: request.sessionUuid,
    parentRunUuid: request.runUuid,
    sourceIds: [
      ...new Set(
        request.content.flatMap((item) =>
          item.type === "media"
            ? item.value.filter((media) => media.type === "video").map((media) => media.value.uuid)
            : [],
        ),
      ),
    ],
  });
}

// Core attaches the retained request budget/cache to this provider object. Keep
// its identity through player remounts and local tracker reruns for the same run.
const providers = new WeakMap<OutputRunContext, Map<string, FrameRecoveryProvider>>();

const recoveryReference = ({ anchor, image }: RecoveryInput["references"][number]) => ({
  anchor: { id: anchor.id, time: anchor.time, coordinates: anchor.coordinates },
  image,
});

export function getTrackingRecoveryProvider(
  context: OutputRunContext | null,
  sourceId: string | undefined,
): FrameRecoveryProvider | undefined {
  if (!context || !sourceId || !context.sourceIds.includes(sourceId)) return undefined;
  const cached = providers.get(context) ?? new Map<string, FrameRecoveryProvider>();
  const existing = cached.get(sourceId);
  if (existing) return existing;

  const provider: FrameRecoveryProvider = Object.freeze({
    key: JSON.stringify([
      context.parentRunUuid,
      sourceId,
      context.model.provider,
      context.model.name,
      RECOVERY_PROMPT_VERSION,
    ]),
    label: context.modelLabel,
    async recover(input: RecoveryInput, signal: AbortSignal): Promise<RecoveryOutcome> {
      signal.throwIfAborted();
      const startedAt = performance.now();
      const fields = {
        parent_run_id: context.parentRunUuid,
        session_id: context.sessionUuid,
        model: context.model.name,
        provider: context.model.provider,
        source_id: sourceId,
        track_id: input.interval.trackId,
        target_timestamp: input.target.timestamp,
        prompt_version: RECOVERY_PROMPT_VERSION,
      };
      const track = (event: string, extra: Record<string, unknown> = {}) => {
        try {
          analytics.track(event, { ...fields, ...extra, timestamp: Date.now() });
        } catch {
          // Telemetry must never block a recovery request or change its result.
        }
      };
      track(ANALYTICS_EVENTS.TRACKING_RECOVERY_STARTED);
      try {
        const result = await demoModelApi.recoverTrack(
          {
            model: context.model,
            sessionUuid: context.sessionUuid,
            parentRunUuid: context.parentRunUuid,
            sourceId,
            trackId: input.interval.trackId,
            trackLabel: input.trackLabel,
            target: input.target,
            references: [recoveryReference(input.references[0]), recoveryReference(input.references[1])],
          },
          signal,
        );
        signal.throwIfAborted();
        track(ANALYTICS_EVENTS.TRACKING_RECOVERY_COMPLETED, {
          status: result.status,
          request_id: result.requestId,
          duration_ms: performance.now() - startedAt,
          prompt_tokens: result.usage?.promptTokens,
          completion_tokens: result.usage?.completionTokens,
          total_tokens: result.usage?.totalTokens,
        });
        return result;
      } catch (error) {
        track(
          signal.aborted ? ANALYTICS_EVENTS.TRACKING_RECOVERY_CANCELLED : ANALYTICS_EVENTS.TRACKING_RECOVERY_FAILED,
          {
            duration_ms: performance.now() - startedAt,
            error_type: error instanceof Error ? error.name : "unknown",
          },
        );
        throw error;
      }
    },
  });
  cached.set(sourceId, provider);
  providers.set(context, cached);
  return provider;
}
